import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  Table } from '@tanstack/react-table';
import {
  createHolisticRubricColumns,
} from '@/components/staffer/ExerciseBuilder/Rubric/AddRubricModal/HolisticRubric/HolisticRubric.utils';
import {
  useQuestionRubric,
  IHolisticRubricDescription,
} from '@/components/staffer/ExerciseBuilder/Rubric/useQuestionRubric';
import EditorDescriptionCell
  from '@/components/staffer/ExerciseBuilder/Rubric/AddRubricModal/HolisticRubric/EditorDescriptionCell';

type UseHolisticRubricDataArgs = {
  questionId?: number;
  locale?: Lowercase<Locale>;
};

interface UseHolisticRubricDataReturn {
  data: IHolisticRubricDescription[];
  setData: React.Dispatch<React.SetStateAction<IHolisticRubricDescription[]>>;
  table: Table<IHolisticRubricDescription>;
}

const useHolisticRubricEditor = ({
  questionId,
  locale = 'en',
} : UseHolisticRubricDataArgs)
: UseHolisticRubricDataReturn => {
  const DEFAULT_HOLISTIC_RUBRIC_DATA: IHolisticRubricDescription[] = [
    { level_id: null, description_id: null, score: 4, description_en: '', description_es: '' },
    { level_id: null, description_id: null, score: 3, description_en: '', description_es: '' },
    { level_id: null, description_id: null, score: 2, description_en: '', description_es: '' },
    { level_id: null, description_id: null, score: 1, description_en: '', description_es: '' },
  ];

  const [data, setData] = useState<IHolisticRubricDescription[]>(DEFAULT_HOLISTIC_RUBRIC_DATA);

  const { data: fetchedData, isLoading } = useQuestionRubric({ questionId });

  useEffect(() => {
    if (!isLoading && fetchedData && fetchedData?.rubric_type === 'holistic') {
      setData(fetchedData.rubric_data as IHolisticRubricDescription[]);
    }
  }, [fetchedData, isLoading]);

  const handleDescriptionChange = useCallback(
    (score: number, newDescription: string) => setData(prevData => prevData.map(
      (item) => {
        if (item.score === score) {
          return {
            ...item,
            [locale === 'en' ? 'description_en' : 'description_es']: newDescription,
          };
        }
        return item;
      },
    )),
    [locale],
  );

  const columns = useMemo(
    () => createHolisticRubricColumns({
      DescriptionCell: EditorDescriptionCell,
      cellProps: { onDescriptionChange: handleDescriptionChange },
      locale,
    }),
    [locale, handleDescriptionChange],
  );

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
  });

  return { data, setData, table };
};

export default useHolisticRubricEditor;
